import { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Heart, Plus, Calendar as CalendarIcon, LogOut, Trash2, Edit, Clock, Image as ImageIcon, Sparkles, Lightbulb, MessageCircle, Settings, CheckSquare, AlertTriangle, Flower2, Menu, X, Cloud, CalendarPlus } from 'lucide-react';
import { format, differenceInDays, parseISO, isFuture } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AIChat from '@/components/AIChat';
import InstallPWA from '@/components/InstallPWA';
import { useServiceWorker } from '@/hooks/useServiceWorker';
import { useNavigate } from 'react-router-dom';
import { SEOHelmet, SEO_CONFIG } from '@/components/SEOHelmet';
import Footer from '@/components/Footer';
import { generateGoogleCalendarLink, downloadICSFile, generateOutlookLink } from '@/utils/calendarUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = {
  meeting: { label: 'İlk Tanışma', color: 'bg-pink-500', icon: '💝' },
  engagement: { label: 'Nişan', color: 'bg-purple-500', icon: '💍' },
  wedding: { label: 'Evlilik', color: 'bg-red-500', icon: '💒' },
  birthday: { label: 'Doğum Günü', color: 'bg-yellow-500', icon: '🎂' },
  anniversary: { label: 'Yıldönümü', color: 'bg-blue-500', icon: '🎉' },
  other: { label: 'Diğer', color: 'bg-gray-500', icon: '⭐' }
};

const LOADING_MESSAGES = [
  "Sizin için en iyisini bulmaya çalışıyoruz... ✨",
  "Unutulmaz bir plan hazırlanıyor... 💝",
  "Özel günleriniz için fikirler üretiliyor... 🎨",
  "Romantik öneriler oluşturuluyor... 💕",
  "AI sihirini çalıştırıyoruz... 🪄",
  "Mükemmel anlar için hazırlık yapılıyor... 🌟",
  "Hayal ettiğiniz gibi bir plan tasarlanıyor... 💭",
  "Sevdiklerinizi mutlu edecek fikirler geliyor... 💖"
];

const CHECKLIST_LOADING_MESSAGES = [
  "Yapılacaklar listesi hazırlanıyor... 📝",
  "Her detay düşünülüyor... ✅",
  "Hiçbir şeyi unutmamanız için çalışıyoruz... 🎯",
  "Mükemmel bir organizasyon için adımlar belirleniyor... 📋",
  "Sizin için özel görevler oluşturuluyor... ⭐",
  "En pratik çözümler aranıyor... 💡",
  "Her şeyin yerli yerinde olması için plan yapılıyor... 🗂️",
  "Detaylı bir checklist hazırlanıyor... 📌"
];

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { subscribeToPush, isSubscribed } = useServiceWorker();
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: new Date(),
    category: 'anniversary',
    notes: '',
    photo_base64: null,
    reminder_days_before: 1
  });
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [chatOpen, setChatOpen] = useState(false);
  const [recommendationOpen, setRecommendationOpen] = useState(false);
  const [selectedDateForRec, setSelectedDateForRec] = useState(null);
  const [recommendations, setRecommendations] = useState('');
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [selectedDateForChecklist, setSelectedDateForChecklist] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [loadingChecklistMessageIndex, setLoadingChecklistMessageIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(user);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherForecastOpen, setWeatherForecastOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [selectedDateForCalendar, setSelectedDateForCalendar] = useState(null);


  useEffect(() => {
    // Check if new user and redirect to profile
    const isNewUser = localStorage.getItem('is_new_user');
    if (isNewUser === 'true') {
      localStorage.removeItem('is_new_user');
      navigate('/profile');
      toast.info('Lütfen profilinizi tamamlayın');
      return;
    }
    
    fetchDates();
    checkNotificationPermission();
    fetchUserProfile();
    fetchWeather();
  }, []);

  // Update page title when user profile changes
  useEffect(() => {
    if (userProfile?.partner_name && userProfile?.my_name) {
      document.title = `${userProfile.partner_name} & ${userProfile.my_name} - Remindora`;
    } else if (userProfile?.partner_name) {
      document.title = `${userProfile.partner_name} - Remindora`;
    } else {
      document.title = 'Remindora';
    }
  }, [userProfile]);


  // Check if user still exists (admin might have deleted)
  useEffect(() => {
    const checkUserExists = async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${API}/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        if (error.response?.status === 401) {
          // User deleted or token invalid
          toast.error('Hesabınız silinmiş. Çıkış yapılıyor...');
          setTimeout(() => {
            onLogout();
          }, 2000);
        }
      }
    };

    // Check immediately
    checkUserExists();

    // Check every 10 seconds
    const interval = setInterval(checkUserExists, 10000);
    
    return () => clearInterval(interval);
  }, [onLogout]);


  // Rotate loading messages every 7 seconds
  useEffect(() => {
    if (loadingRecommendations) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
      }, 7000);
      
      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0); // Reset when loading stops
    }
  }, [loadingRecommendations]);

  // Rotate checklist loading messages every 7 seconds
  useEffect(() => {
    if (loadingChecklist) {
      const interval = setInterval(() => {
        setLoadingChecklistMessageIndex((prev) => (prev + 1) % CHECKLIST_LOADING_MESSAGES.length);
      }, 7000);
      
      return () => clearInterval(interval);
    } else {
      setLoadingChecklistMessageIndex(0); // Reset when loading stops
    }
  }, [loadingChecklist]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.profile) {
        // Merge user data with profile data
        const updatedUser = {
          ...user,
          ...response.data.profile
        };
        setUserProfile(updatedUser);
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
    }
  };

  const fetchWeather = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/weather/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.error === 'no_city') {
        // User hasn't set city - show message
        setWeather({
          no_city: true,
          message: response.data.message
        });
      } else if (!response.data.error) {
        setWeather(response.data);
      }
    } catch (error) {
      console.error('Weather fetch error:', error);
      // Silently fail - weather is not critical
    } finally {
      setWeatherLoading(false);
    }
  };

  const checkNotificationPermission = async () => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        const subscription = await subscribeToPush();
        
        if (subscription) {
          // Send subscription to backend
          try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
              `${API}/notifications/subscribe`,
              { subscription: subscription.toJSON() },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('✅ Subscription saved to backend:', response.data);
            toast.success('Push bildirimleri aktif! Uygulama kapalıyken bile bildirim alacaksınız.');
          } catch (error) {
            console.error('❌ Subscription save error:', error);
            toast.error('Push bildirimleri kaydedilemedi. Lütfen tekrar deneyin.');
          }
        } else {
          console.error('❌ Failed to create push subscription');
          toast.error('Push bildirimleri aktif edilemedi. Lütfen tekrar deneyin.');
        }
      }
    }
  };

  const fetchDates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDates(response.data);
    } catch (error) {
      toast.error('Tarihler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Dosya boyutu 5MB\'dan küçük olmalı');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        date: format(formData.date, 'yyyy-MM-dd')
      };

      if (editingDate) {
        await axios.put(`${API}/dates/${editingDate.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tarih güncellendi!');
      } else {
        await axios.post(`${API}/dates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Yeni tarih eklendi!');
      }

      fetchDates();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu tarihi silmek istediğinizden emin misiniz?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tarih silindi');
      fetchDates();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleEdit = (date) => {
    setEditingDate(date);
    setFormData({
      title: date.title,
      date: parseISO(date.date),
      category: date.category,
      notes: date.notes,
      photo_base64: date.photo_base64,
      reminder_days_before: date.reminder_days_before
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingDate(null);
    setFormData({
      title: '',
      date: new Date(),
      category: 'anniversary',
      notes: '',
      photo_base64: null,
      reminder_days_before: 1
    });
  };

  const calculateCountdown = (dateStr) => {
    const targetDate = parseISO(dateStr);
    const today = new Date();
    const days = differenceInDays(targetDate, today);
    
    if (days === 0) return { text: 'Bugün!', color: 'text-red-500' };
    if (days < 0) return { text: `${Math.abs(days)} gün önce`, color: 'text-gray-400' };
    if (days === 1) return { text: 'Yarın', color: 'text-orange-500' };
    return { text: `${days} gün sonra`, color: 'text-blue-600' };
  };

  const getRecommendations = async (dateId) => {
    setSelectedDateForRec(dateId);
    setRecommendationOpen(true);
    setLoadingRecommendations(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/recommendations`,
        { date_id: dateId, city: 'Istanbul' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecommendations(response.data.recommendations);
    } catch (error) {
      toast.error('Öneriler alınamadı');
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const openChecklist = async (date) => {
    setSelectedDateForChecklist(date);
    setChecklistOpen(true);
    setLoadingChecklist(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/dates/${date.id}/checklist`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChecklistItems(response.data);
    } catch (error) {
      toast.error('Kontrol listesi yüklenemedi');
    } finally {
      setLoadingChecklist(false);
    }
  };

  const toggleChecklistItem = async (itemId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/dates/${selectedDateForChecklist.id}/checklist/${itemId}`,
        { is_completed: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setChecklistItems(items =>
        items.map(item =>
          item.id === itemId ? { ...item, is_completed: !currentStatus } : item
        )
      );
      
      toast.success(!currentStatus ? 'Tamamlandı işaretlendi' : 'İşaret kaldırıldı');
    } catch (error) {
      toast.error('Güncellenemedi');
    }
  };

  const calculateChecklistProgress = (dateId) => {
    const items = checklistItems.filter(item => item.date_id === dateId);
    if (items.length === 0) return 0;
    const completed = items.filter(item => item.is_completed).length;
    return Math.round((completed / items.length) * 100);
  };

  return (
    <>
      <SEOHelmet {...SEO_CONFIG.dashboard} />
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50/30 to-neutral-50">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-neutral-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <img src="/logo-full.png" alt="Remindora - Özel Günler Dashboard" className="h-10 sm:h-12 w-auto object-contain bg-white rounded-lg px-2 py-1" />
              {userProfile?.partner_name && userProfile?.my_name && (
                <div className="block">
                  <p className="text-xs sm:text-sm text-neutral-600">
                    <span className="font-bold uppercase">{userProfile.partner_name}</span> & <span className="font-bold uppercase">{userProfile.my_name}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Weather Widget in Header */}
            {!weatherLoading && weather && weather.no_city && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg cursor-pointer hover:bg-amber-100" onClick={() => navigate('/profile')}>
                <span className="text-xs text-amber-800 font-medium">ℹ️ {weather.message}</span>
              </div>
            )}
            {!weatherLoading && weather && weather.current && (
              <div className="flex items-center gap-2 px-2 py-1.5 bg-gradient-to-r from-blue-50/50 to-sky-50/50 border border-blue-200/30 rounded-lg">
                <img 
                  src={`https:${weather.current.condition_icon}`} 
                  alt={weather.current.condition_text}
                  className="w-6 h-6"
                />
                <div className="flex flex-col">
                  <span className="text-[10px] text-neutral-600 leading-tight font-bold">{format(new Date(), 'dd MMMM yyyy', { locale: tr })}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-blue-900">{Math.round(weather.current.temp_c)}°C</span>
                    <span className="text-xs text-neutral-600 hidden sm:inline">{weather.city}</span>
                  </div>
                </div>
                {weather.forecast && weather.forecast.length > 0 && (
                  <button
                    onClick={() => setWeatherForecastOpen(!weatherForecastOpen)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium ml-1"
                  >
                    {weatherForecastOpen ? '▲' : '▼'}
                  </button>
                )}
              </div>
            )}

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-2">
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="profile-button"
              >
                <Settings className="w-4 h-4" />
                Profil
              </Button>
              <Button
                onClick={() => setChatOpen(true)}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="open-ai-chat-button"
              >
                <MessageCircle className="w-4 h-4" />
                Remindora Asistan
              </Button>
              {notificationPermission !== 'granted' && (
                <Button
                  onClick={requestNotificationPermission}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  data-testid="enable-notifications-button"
                >
                  <Clock className="w-4 h-4" />
                  Bildirimleri Aç
                </Button>
              )}
              <Button
                onClick={onLogout}
                variant="ghost"
                size="sm"
                className="gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Çıkış
              </Button>
            </div>

            {/* Mobile Hamburger Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t border-neutral-200 space-y-2 animate-in slide-in-from-top pb-4">
              <div className="px-4 mb-2">
                <InstallPWA />
              </div>
              <Button
                onClick={() => {
                  navigate('/profile');
                  setMobileMenuOpen(false);
                }}
                variant="outline"
                className="w-full justify-start gap-2"
                data-testid="mobile-profile-button"
              >
                <Settings className="w-4 h-4" />
                Profil
              </Button>
              <Button
                onClick={() => {
                  setChatOpen(true);
                  setMobileMenuOpen(false);
                }}
                variant="outline"
                className="w-full justify-start gap-2"
                data-testid="mobile-ai-chat-button"
              >
                <MessageCircle className="w-4 h-4" />
                Remindora Asistan
              </Button>
              {notificationPermission !== 'granted' && (
                <Button
                  onClick={() => {
                    requestNotificationPermission();
                    setMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="w-full justify-start gap-2"
                  data-testid="mobile-notifications-button"
                >
                  <Clock className="w-4 h-4" />
                  Bildirimleri Aç
                </Button>
              )}
              <Button
                onClick={() => {
                  onLogout();
                  setMobileMenuOpen(false);
                }}
                variant="ghost"
                className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="mobile-logout-button"
              >
                <LogOut className="w-4 h-4" />
                Çıkış
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Weather Forecast Dropdown - Below Header */}
      {weatherForecastOpen && !weatherLoading && weather && weather.forecast && weather.forecast.length > 0 && (
        <div className="bg-white/95 backdrop-blur-sm border-b border-blue-200 shadow-sm animate-in slide-in-from-top">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="grid grid-cols-3 gap-3">
              {weather.forecast.map((day, index) => (
                <div key={index} className="text-center bg-gradient-to-br from-blue-50/50 to-sky-50/50 border border-blue-200/30 rounded-lg p-2">
                  <p className="text-xs text-neutral-600 font-medium mb-1">
                    {format(parseISO(day.date), 'EEEE', { locale: tr })}
                  </p>
                  <p className="text-xs text-neutral-500 mb-2">
                    {format(parseISO(day.date), 'dd MMMM', { locale: tr })}
                  </p>
                  <img 
                    src={`https:${day.condition_icon}`} 
                    alt={day.condition_text}
                    className="w-10 h-10 mx-auto mb-2"
                  />
                  <p className="text-sm font-bold text-blue-900 mb-1">
                    {Math.round(day.max_temp_c)}° / {Math.round(day.min_temp_c)}°
                  </p>
                  <p className="text-xs text-neutral-600 mb-1">{day.condition_text}</p>
                  {day.chance_of_rain > 0 && (
                    <p className="text-xs text-blue-600">💧 Yağmur: {day.chance_of_rain}%</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Date Button */}
        <div className="mb-8">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-lg hover-lift"
                data-testid="add-date-button"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Yeni Özel Gün Ekle</span>
                <span className="sm:hidden">Yeni Gün Ekle</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="date-dialog">
              <DialogHeader>
                <DialogTitle>{editingDate ? 'Tarihi Düzenle' : 'Yeni Özel Gün'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Başlık</Label>
                  <Input
                    id="title"
                    placeholder="Örn: İlk tanışma günümüz"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="date-title-input"
                  />
                </div>

                <div className="space-y-3">
                  <Label>Doğum Tarihi</Label>
                  
                  {/* Year and Month Selection */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-neutral-600">Yıl</Label>
                      <Select
                        value={formData.date.getFullYear().toString()}
                        onValueChange={(value) => {
                          const newDate = new Date(formData.date);
                          newDate.setFullYear(parseInt(value));
                          setFormData({ ...formData, date: newDate });
                        }}
                      >
                        <SelectTrigger data-testid="year-select">
                          <SelectValue placeholder="Yıl seçin" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {Array.from({ length: 76 }, (_, i) => 2025 - i).map(year => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-neutral-600">Ay</Label>
                      <Select
                        value={(formData.date.getMonth() + 1).toString()}
                        onValueChange={(value) => {
                          const newDate = new Date(formData.date);
                          newDate.setMonth(parseInt(value) - 1);
                          setFormData({ ...formData, date: newDate });
                        }}
                      >
                        <SelectTrigger data-testid="month-select">
                          <SelectValue placeholder="Ay seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                            'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'].map((month, index) => (
                            <SelectItem key={index} value={(index + 1).toString()}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Calendar for Day Selection */}
                  <div className="border rounded-lg p-3 bg-neutral-50">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => {
                        if (date) {
                          setFormData({ ...formData, date });
                        }
                      }}
                      month={formData.date}
                      onMonthChange={(newMonth) => {
                        setFormData({ ...formData, date: newMonth });
                      }}
                      disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      locale={tr}
                      className="w-full"
                      data-testid="calendar"
                    />
                  </div>
                  
                  <p className="text-xs text-neutral-600 bg-blue-50 p-2 rounded">
                    ✨ Seçilen: <span className="font-semibold">{format(formData.date, 'dd MMMM yyyy', { locale: tr })}</span>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Kategori</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORIES).map(([key, { label, icon }]) => (
                        <SelectItem key={key} value={key}>
                          {icon} {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notlar</Label>
                  <Textarea
                    id="notes"
                    placeholder="Bu gün hakkında notlar..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    data-testid="date-notes-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="photo">Fotoğraf</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="photo"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="flex-1"
                      data-testid="photo-upload-input"
                    />
                    {formData.photo_base64 && (
                      <div className="relative w-16 h-16 rounded-lg overflow-hidden border">
                        <img
                          src={formData.photo_base64}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reminder">Kaç gün önce hatırlat?</Label>
                  <Select
                    value={formData.reminder_days_before.toString()}
                    onValueChange={(value) => setFormData({ ...formData, reminder_days_before: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="reminder-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 gün önce</SelectItem>
                      <SelectItem value="3">3 gün önce</SelectItem>
                      <SelectItem value="7">1 hafta önce</SelectItem>
                      <SelectItem value="14">2 hafta önce</SelectItem>
                      <SelectItem value="30">1 ay önce</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-date-button">
                    {editingDate ? 'Güncelle' : 'Kaydet'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                    data-testid="cancel-button"
                  >
                    İptal
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Dates Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-neutral-600">Yükleniyor...</p>
          </div>
        ) : dates.length === 0 ? (
          <div className="text-center py-12" data-testid="empty-state">
            <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="w-10 h-10 text-neutral-400" />
            </div>
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">Henüz özel gün eklemediniz</h3>
            <p className="text-neutral-600 mb-4">İlk özel gününüzü ekleyerek başlayın!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dates.map((date, index) => {
              const countdown = calculateCountdown(date.date);
              const category = CATEGORIES[date.category];
              return (
                <Card
                  key={date.id}
                  className="hover-lift glass overflow-hidden animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`date-card-${date.id}`}
                >
                  {date.photo_base64 && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={date.photo_base64}
                        alt={date.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 ${category.color} text-white text-xs font-medium rounded-full`}>
                            {category.icon} {category.label}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-neutral-900 mb-1">{date.title}</h3>
                        <p className="text-sm text-neutral-600">
                          {format(parseISO(date.date), 'dd MMMM yyyy', { locale: tr })}
                          {(() => {
                            // Show anniversary for all recurring dates
                            if (date.original_date) {
                              const originalYear = new Date(date.original_date).getFullYear();
                              const currentYear = new Date(date.date).getFullYear();
                              const years = currentYear - originalYear;
                              
                              if (years > 0) {
                                // Special text for birthday
                                if (date.category === 'birthday') {
                                  return (
                                    <span className="ml-2 text-xs text-yellow-600 font-semibold">
                                      ({years}. Yaş Günü)
                                    </span>
                                  );
                                } else {
                                  return (
                                    <span className="ml-2 text-xs text-purple-600 font-semibold">
                                      ({years}. Yıldönümü)
                                    </span>
                                  );
                                }
                              }
                            }
                            return null;
                          })()}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(date)}
                          data-testid={`edit-button-${date.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(date.id)}
                          data-testid={`delete-button-${date.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className={`inline-flex items-center gap-2 px-4 py-2 bg-neutral-50 rounded-lg`}>
                        <Clock className={`w-5 h-5 ${countdown.color}`} />
                        <span className={`font-semibold ${countdown.color}`}>{countdown.text}</span>
                      </div>
                    </div>

                    {date.notes && (
                      <p className="text-sm text-neutral-600 line-clamp-2 mb-3">{date.notes}</p>
                    )}

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-neutral-500">
                          <Clock className="w-3 h-3" />
                          {date.reminder_days_before} gün önce hatırlatma
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-blue-600 hover:text-blue-700"
                            onClick={() => {
                              setSelectedDateForCalendar(date);
                              setCalendarModalOpen(true);
                            }}
                            data-testid={`add-to-calendar-${date.id}`}
                          >
                            <CalendarPlus className="w-4 h-4" />
                            Takvime Ekle
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-purple-600 hover:text-purple-700"
                            onClick={() => getRecommendations(date.id)}
                            data-testid={`get-recommendations-${date.id}`}
                          >
                            <Sparkles className="w-4 h-4" />
                            AI Önerileri
                          </Button>
                        </div>
                      </div>
                      
                      {(() => {
                        const targetDate = parseISO(date.date);
                        const today = new Date();
                        const daysUntil = differenceInDays(targetDate, today);
                        return daysUntil > 0 && daysUntil <= 30;
                      })() && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                            onClick={() => openChecklist(date)}
                            data-testid={`open-checklist-${date.id}`}
                          >
                            {(() => {
                              const targetDate = parseISO(date.date);
                              const today = new Date();
                              const daysUntil = differenceInDays(targetDate, today);
                              return daysUntil <= 7;
                            })() ? (
                              <AlertTriangle className="w-4 h-4" />
                            ) : (
                              <CheckSquare className="w-4 h-4" />
                            )}
                            Hazırlık Kontrol Listesi
                          </Button>
                          
                          {(() => {
                            const targetDate = parseISO(date.date);
                            const today = new Date();
                            const daysUntil = differenceInDays(targetDate, today);
                            return daysUntil === 1;
                          })() && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full gap-2 border-rose-300 text-rose-700 hover:bg-rose-50 animate-pulse"
                              onClick={() => window.open('https://www.ciceksepeti.com/', '_blank')}
                              data-testid={`send-flowers-${date.id}`}
                            >
                              <Flower2 className="w-4 h-4" />
                              🌹 Çiçek Gönder (Yarın!)
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* AI Chat */}
      <AIChat isOpen={chatOpen} onClose={() => setChatOpen(false)} />

      {/* Recommendations Modal */}
      <Dialog open={recommendationOpen} onOpenChange={setRecommendationOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="recommendations-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              AI Önerileri ve Plan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingRecommendations ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-purple-700 animate-pulse transition-all duration-500">
                    {LOADING_MESSAGES[loadingMessageIndex]}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-purple-900 mb-4 mt-6 first:mt-0 border-b-2 border-purple-300 pb-2" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-xl font-bold text-purple-800 mb-3 mt-5 first:mt-0" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-lg font-semibold text-purple-700 mb-2 mt-4 first:mt-0" {...props} />,
                    p: ({node, ...props}) => <p className="text-base text-neutral-700 leading-relaxed mb-3" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-outside ml-6 mb-3 space-y-2" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-outside ml-6 mb-3 space-y-2" {...props} />,
                    li: ({node, ...props}) => <li className="text-base text-neutral-700 leading-relaxed pl-1" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-purple-800" {...props} />,
                    em: ({node, ...props}) => <em className="italic text-neutral-600" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-purple-400 bg-purple-50 pl-4 py-2 italic text-neutral-600 my-3 rounded-r" {...props} />,
                    code: ({node, inline, ...props}) => 
                      inline ? 
                        <code className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-sm font-mono" {...props} /> :
                        <code className="block bg-neutral-800 text-neutral-100 p-4 rounded my-3 overflow-x-auto text-sm font-mono" {...props} />
                  }}
                >
                  {recommendations}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Checklist Modal */}
      <Dialog open={checklistOpen} onOpenChange={setChecklistOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="checklist-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="w-6 h-6 text-orange-600" />
              Hazırlık Kontrol Listesi
            </DialogTitle>
            {selectedDateForChecklist && (
              <p className="text-sm text-neutral-600">
                {selectedDateForChecklist.title} - {format(parseISO(selectedDateForChecklist.date), 'dd MMMM yyyy', { locale: tr })}
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingChecklist ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-orange-700 animate-pulse transition-all duration-500">
                    {CHECKLIST_LOADING_MESSAGES[loadingChecklistMessageIndex]}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Progress Bar */}
                <div className="bg-neutral-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700">İlerleme</span>
                    <span className="text-sm font-bold text-orange-600">
                      {checklistItems.filter(item => item.is_completed).length} / {checklistItems.length}
                    </span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${(checklistItems.filter(item => item.is_completed).length / checklistItems.length) * 100}%`
                      }}
                    />
                  </div>
                </div>

                {/* Checklist Items by Category */}
                {['gift', 'reservation', 'clothing', 'preparation', 'other'].map(category => {
                  const categoryItems = checklistItems.filter(item => item.category === category);
                  if (categoryItems.length === 0) return null;
                  
                  const categoryNames = {
                    gift: '🎁 Hediye',
                    reservation: '📅 Rezervasyon',
                    clothing: '👔 Kıyafet',
                    preparation: '📋 Hazırlık',
                    other: '✨ Diğer'
                  };
                  
                  return (
                    <div key={category} className="space-y-2">
                      <h3 className="font-semibold text-neutral-900 text-sm">{categoryNames[category]}</h3>
                      <div className="space-y-2">
                        {categoryItems.map(item => (
                          <label
                            key={item.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              item.is_completed
                                ? 'bg-green-50 border-green-300'
                                : 'bg-white border-neutral-200 hover:border-orange-300'
                            }`}
                            data-testid={`checklist-item-${item.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={item.is_completed}
                              onChange={() => toggleChecklistItem(item.id, item.is_completed)}
                              className="w-5 h-5 rounded border-neutral-300 text-orange-600 focus:ring-orange-500"
                            />
                            <span className={`flex-1 ${item.is_completed ? 'line-through text-neutral-500' : 'text-neutral-900'}`}>
                              {item.item_text}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Warning for incomplete items (7 days or less) */}
                {selectedDateForChecklist && 
                 differenceInDays(parseISO(selectedDateForChecklist.date), new Date()) <= 7 &&
                 checklistItems.some(item => !item.is_completed) && (
                  <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-orange-900 mb-1">
                        ⚠️ 1 Hafta Kaldı!
                      </p>
                      <p className="text-sm text-orange-800">
                        {checklistItems.filter(item => !item.is_completed).length} görev henüz tamamlanmadı. 
                        Lütfen hazırlıklarınızı kontrol edin!
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add to Calendar Modal */}
      <Dialog open={calendarModalOpen} onOpenChange={setCalendarModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-blue-600" />
              Takvime Ekle
            </DialogTitle>
            {selectedDateForCalendar && (
              <p className="text-sm text-neutral-600">
                {selectedDateForCalendar.title} - {format(parseISO(selectedDateForCalendar.date), 'dd MMMM yyyy', { locale: tr })}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-3 py-4">
            <p className="text-sm text-neutral-600 mb-4">
              Hangi takvime eklemek istersiniz?
            </p>

            {/* Google Calendar */}
            <Button
              onClick={() => {
                if (selectedDateForCalendar) {
                  const link = generateGoogleCalendarLink(
                    selectedDateForCalendar.title,
                    selectedDateForCalendar.date,
                    selectedDateForCalendar.notes
                  );
                  window.open(link, '_blank');
                  setCalendarModalOpen(false);
                  toast.success('Google Calendar açılıyor...');
                }
              }}
              variant="outline"
              className="w-full justify-start gap-3 h-12"
            >
              <div className="w-8 h-8 rounded bg-white flex items-center justify-center border">
                <img 
                  src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/googlecalendar.svg" 
                  alt="Google Calendar"
                  className="w-6 h-6"
                  style={{ filter: 'none' }}
                />
              </div>
              <div className="text-left">
                <p className="font-medium">Google Calendar</p>
                <p className="text-xs text-neutral-500">Tarayıcıda açılır</p>
              </div>
            </Button>

            {/* Apple Calendar */}
            <Button
              onClick={() => {
                if (selectedDateForCalendar) {
                  downloadICSFile(
                    selectedDateForCalendar.title,
                    selectedDateForCalendar.date,
                    selectedDateForCalendar.notes
                  );
                  setCalendarModalOpen(false);
                  toast.success('Takvim dosyası indirildi! Dosyayı açarak takviminize ekleyin.');
                }
              }}
              variant="outline"
              className="w-full justify-start gap-3 h-12"
            >
              <div className="w-8 h-8 rounded bg-white flex items-center justify-center border">
                <img 
                  src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/apple.svg" 
                  alt="Apple Calendar"
                  className="w-6 h-6"
                />
              </div>
              <div className="text-left">
                <p className="font-medium">Apple Calendar</p>
                <p className="text-xs text-neutral-500">.ics dosyası indirilir</p>
              </div>
            </Button>

            {/* Outlook Calendar */}
            <Button
              onClick={() => {
                if (selectedDateForCalendar) {
                  downloadICSFile(
                    selectedDateForCalendar.title,
                    selectedDateForCalendar.date,
                    selectedDateForCalendar.notes
                  );
                  setCalendarModalOpen(false);
                  toast.success('Takvim dosyası indirildi! Outlook uygulamanızda açılacak.');
                }
              }}
              variant="outline"
              className="w-full justify-start gap-3 h-12"
            >
              <div className="w-8 h-8 rounded bg-white flex items-center justify-center border">
                <img 
                  src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/microsoftoutlook.svg" 
                  alt="Outlook"
                  className="w-6 h-6"
                  style={{ filter: 'invert(24%) sepia(99%) saturate(3272%) hue-rotate(197deg) brightness(91%) contrast(101%)' }}
                />
              </div>
              <div className="text-left">
                <p className="font-medium">Outlook Calendar</p>
                <p className="text-xs text-neutral-500">.ics dosyası - Outlook'ta açılır</p>
              </div>
            </Button>

            {/* Other Calendars */}
            <Button
              onClick={() => {
                if (selectedDateForCalendar) {
                  downloadICSFile(
                    selectedDateForCalendar.title,
                    selectedDateForCalendar.date,
                    selectedDateForCalendar.notes
                  );
                  setCalendarModalOpen(false);
                  toast.success('Takvim dosyası indirildi! Herhangi bir takvim uygulaması ile açabilirsiniz.');
                }
              }}
              variant="outline"
              className="w-full justify-start gap-3 h-12"
            >
              <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center">
                <span className="text-lg">📆</span>
              </div>
              <div className="text-left">
                <p className="font-medium">Diğer Takvimler</p>
                <p className="text-xs text-neutral-500">.ics dosyası (evrensel format)</p>
              </div>
            </Button>
          </div>

          <Button
            onClick={() => setCalendarModalOpen(false)}
            variant="ghost"
            className="w-full"
          >
            İptal
          </Button>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
    </>
  );
}
